import { COLORS2 } from '@/Client/Styles';
import { ScanPartOptimized } from '@kaiizen/kai-scan-template-system';
import { useEffect, useRef } from 'react';

import { css } from '@emotion/react';
import { ApiScanNote, ScanNoteType, ScanPartApprovalStatus } from '@kaiizen/kai-pro-api';
import { NoteIcon } from '@kaiizen/kai-shiki/react';

import { useShowNotes } from '@/Client/State';
import { NotesCountBubble } from '@/Client/Widgets';

const NOTES_ICON_STYLE = css`
  label: NOTES_ICON_STYLE;
  padding: 0;
  display: flex;
  position: relative;
  width: 26px;
  height: 26px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover {
    transform: scale(1.2);
  }
`;

const getNotesCountBackgroundColor = (approvalStatus: ScanPartApprovalStatus = 'needsReview'): string => {
  switch (approvalStatus) {
    case 'approved':
      return COLORS2.alwaysDark.text.green;
    case 'editRequested':
      return COLORS2.alwaysDark.yellow;
    case 'editResolved':
      return COLORS2.alwaysDark.lightBlue;
    case 'needsReview':
      return COLORS2.alwaysDark.green;
  }
};

const getNotesCountTextColor = (approvalStatus: ScanPartApprovalStatus = 'needsReview'): string => {
  switch (approvalStatus) {
    case 'approved':
    case 'editRequested':
    case 'editResolved':
      return COLORS2.alwaysDark.black;
    case 'needsReview':
      return COLORS2.alwaysDark.white;
  }
};

export const FocusedScanPartNotesButton = ({
  scanPart,
  onCreateScanPart,
  notes,
  allowedNoteTypes,
  approvalStatus,
  alwaysDark = false,
}: {
  scanPart?: ScanPartOptimized;
  onCreateScanPart?: () => void;
  notes: ApiScanNote[];
  allowedNoteTypes?: ScanNoteType[];
  approvalStatus?: ScanPartApprovalStatus;
  alwaysDark?: boolean;
}) => {
  const { showNotesEditor } = useShowNotes();
  const pendingNotesOpen = useRef(false);
  const noteCount = notes.length;
  const haveNotes = noteCount > 0;

  useEffect(() => {
    if (scanPart && pendingNotesOpen.current) {
      pendingNotesOpen.current = false;
      showNotesEditor({ scanPart, allowedNoteTypes });
    }
  }, [scanPart, showNotesEditor, allowedNoteTypes]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (scanPart) {
      showNotesEditor({ scanPart, allowedNoteTypes });
    } else if (onCreateScanPart) {
      pendingNotesOpen.current = true;
      onCreateScanPart();
    }
  };

  const iconColor = alwaysDark
    ? (haveNotes ? COLORS2.alwaysDark.text.white : COLORS2.alwaysDark.text.lightGray)
    : haveNotes
    ? 'var(--text)'
    : 'var(--text2)';

  return (
    <div css={NOTES_ICON_STYLE} onClick={handleClick}>
      <NoteIcon size={14} color={iconColor} />
      {haveNotes
        ? (
          <NotesCountBubble
            count={noteCount}
            top={10}
            right={14}
            backgroundColor={getNotesCountBackgroundColor(approvalStatus)}
            textColor={getNotesCountTextColor(approvalStatus)}
          />
        )
        : null}
    </div>
  );
};
